import { ActionContext } from 'vuex';

import { NewValuationFlow, PropertyTypeAndUsage } from '@/types';

import { prefetchListingToSftiMapping, submitImmocheckFormData } from '@/api/ubsProxyApi';
import { captureError } from '@/helpers/RUMService/RUMService';
import { mapImmocheckValuationStoreToUbsPayload } from '@/helpers/UBSMapper/mapper';
import {
  ImmocheckValuationActions,
  ImmocheckValuationActionsType,
  ImmocheckValuationData,
  ImmocheckValuationGetters,
  ImmocheckValuationModule,
  ImmocheckValuationMutations,
  ImmocheckValuationState,
  Lead,
} from './types';

const state = (): ImmocheckValuationState => ({
  step: 0,
  previousStep: null,
  loading: false,
  hasError: false,
  data: {
    propertyType: undefined,
    propertyTypeAndUsage: undefined, // propertyType + classOfProperty
    propertyObjectId: undefined,
    address: {
      lat: 0,
      lon: 0,
      countryCode: 'CH',
      postalCode: '',
      city: '',
      street: '',
      formattedAddress: '',
    },
    livingSpace: undefined,
    lotSize: undefined,
    rooms: 0, // numberOfRooms
    buildYear: undefined,
    classOfProperty: undefined,
    constructionQualityType: undefined,
    stateBuildingType: undefined,
    minergieStandardType: undefined,
    numberOfExternalParking: undefined,
    numberOfSeparateGarages: undefined,
    apartmentType: undefined,
    volumeIncludingGarageInhouse: undefined,
    landArea: undefined,
    houseType: undefined,
    floor: undefined,
    objectPrice: undefined,
  },
  lead: {
    id: '',
  },
  showYearBuildPrefillNotification: false,
  showLotSizePrefillNotification: false,
  navigationDirection: 'forward',
  submitInProgress: false,
  // Tracks if user has performed any navigation action (to determine back button visibility)
  hasUserNavigated: false,
  // Stores property keys that were fetched from the backend but not automatically filled into the form,
  // ensuring these remain available for user editing and persist across navigation (back/next) actions.
  editableProperties: [],
});

const mutations: ImmocheckValuationMutations = {
  setFormLoading(state: ImmocheckValuationState, payload: { status: boolean }): void {
    state.loading = payload.status;
  },
  setSubmitInProgress(state: ImmocheckValuationState, payload: { status: boolean }): void {
    state.submitInProgress = payload.status;
  },
  setEditableProperties(state: ImmocheckValuationState, payload: Array<string>): void {
    state.editableProperties = payload;
  },
  setFormStep(state: ImmocheckValuationState, payload: { step: number }): void {
    // Track the previous step for navigation history
    state.previousStep = state.step;
    state.step = payload.step;
  },
  setFormData(state: ImmocheckValuationState, payload: ImmocheckValuationData): void {
    state.data = { ...state.data, ...payload };
  },
  setLead(state: ImmocheckValuationState, payload: Lead): void {
    state.lead = {
      id: payload.id,
    };
  },
  setPrefillNotificationVisibility(
    state: ImmocheckValuationState,
    {
      field,
      show,
    }: {
      field: 'showYearBuildPrefillNotification' | 'showLotSizePrefillNotification';
      show: boolean;
    },
  ): void {
    state[field] = show;
  },
  setHasError(state: ImmocheckValuationState, hasError: boolean): void {
    state.hasError = hasError;
  },
  setNavigationDirection(state: ImmocheckValuationState, direction: 'forward' | 'backward'): void {
    state.navigationDirection = direction;
  },
  setHasUserNavigated(state: ImmocheckValuationState, hasNavigated: boolean): void {
    state.hasUserNavigated = hasNavigated;
  },
};

const getters: ImmocheckValuationGetters = {
  getNavigationPercent: (state: ImmocheckValuationState) => {
    // Simplified progress calculation - use all possible steps for consistent progress
    const allPossibleSteps = allSteps(state);
    const total = Math.max(allPossibleSteps.length, 1);
    const idx = allPossibleSteps.indexOf(state.step);
    if (idx === -1) return 100; // Return 100% when step exceeds visible list (matches test expectation)
    return Math.round(((idx + 1) / total) * 100);
  },
  getFormData: (state: ImmocheckValuationState): ImmocheckValuationData => state.data,
  getFormStep: (state: ImmocheckValuationState): number => state.step,
  getFormLoading: (state: ImmocheckValuationState): boolean => state.loading,
  getSubmitInProgress: (state: ImmocheckValuationState): boolean => state.submitInProgress,
  getEditableProperties: (state: ImmocheckValuationState): Array<string> =>
    state.editableProperties,
  getLead: (state: ImmocheckValuationState): Lead => state.lead,
  getYearBuildPrefillNotificationState: (state: ImmocheckValuationState): boolean =>
    state.showYearBuildPrefillNotification,
  getLotSizePrefillNotificationState: (state: ImmocheckValuationState): boolean =>
    state.showLotSizePrefillNotification,
  getHasError: (state: ImmocheckValuationState): boolean => state.hasError,
  getNavigationDirection: (state: ImmocheckValuationState): 'forward' | 'backward' =>
    state.navigationDirection,
  getIsCurrentStepVisible: (state: ImmocheckValuationState): boolean => {
    return !isStepHidden(state, state.step);
  },
  getIsFirstVisibleStep: (state: ImmocheckValuationState): boolean => {
    return state.step === getLogicalFirstStep(state);
  },
};

const actions: ImmocheckValuationActions = {
  async navigationNext(context, payload) {
    const currentStep = context.state?.step ?? 0;

    // Respect explicit step if provided (tests expect exact commit)
    if (payload?.step !== undefined) {
      const explicit = clampStep(payload.step);
      context.commit('setNavigationDirection', 'forward');
      context.commit('setFormStep', { step: explicit });
      // Mark that user has started navigating only when step actually changes
      if (explicit !== currentStep) {
        context.commit('setHasUserNavigated', true);
      }
      return;
    }
    const desired = clampStep((context.state?.step ?? 0) + 1);
    const steps = visibleSteps(context.state);
    const idx = closestIndex(steps, desired, 'forward');
    const newStep = steps[idx];

    context.commit('setNavigationDirection', 'forward');
    context.commit('setFormStep', { step: newStep });

    // Mark that user has started navigating only when step actually changes
    if (newStep !== currentStep) {
      context.commit('setHasUserNavigated', true);
    }
  },

  async navigationBack(context, payload) {
    const currentStep = context.state?.step ?? 0;

    // Handle explicit step navigation
    if (payload?.step !== undefined) {
      const explicit = clampStep(payload.step);
      context.commit('setNavigationDirection', 'backward');
      context.commit('setFormStep', { step: explicit });
      if (explicit !== currentStep) context.commit('setHasUserNavigated', true);
      return;
    }

    const targetStep = findPreviousStep(
      (context.state ?? ({} as ImmocheckValuationState)) as ImmocheckValuationState,
    );

    // Execute navigation if valid target found
    if (targetStep !== null) {
      context.commit('setNavigationDirection', 'backward');
      context.commit('setFormStep', { step: targetStep });
      if (targetStep !== currentStep) context.commit('setHasUserNavigated', true);
    }
  },

  navigation(context, step: number) {
    const currentStep = context.state?.step ?? 0;
    const desired = clampStep(step);
    const direction: 'forward' | 'backward' = desired > currentStep ? 'forward' : 'backward';
    const steps = visibleSteps((context.state ?? ({} as any)) as any);
    const idx = closestIndex(steps, desired, direction);
    const newStep = steps[idx];

    context.commit('setNavigationDirection', direction);
    context.commit('setFormStep', { step: newStep });

    // Mark that user has started navigating only when step actually changes
    if (newStep !== currentStep) {
      context.commit('setHasUserNavigated', true);
    }
  },

  async ensureForwardSkips(context) {
    const steps = visibleSteps(context.state);
    if (!steps.length) return;
    const first = steps[0];
    if (first !== context.state.step) {
      context.commit('setNavigationDirection', 'forward');
      context.commit('setFormStep', { step: first });
    }
  },

  setFormData(
    context: ActionContext<ImmocheckValuationState, any>,
    payload: ImmocheckValuationData,
  ) {
    context.commit('setFormData', payload);
  },

  setFormStep(context: ActionContext<ImmocheckValuationState, any>, payload: { step: number }) {
    context.commit('setFormStep', { step: payload.step });
  },

  initData(context: ActionContext<ImmocheckValuationState, any>, type: PropertyTypeAndUsage) {
    context.commit('setFormData', {
      propertyType: type === PropertyTypeAndUsage.CONDOMINIUM ? 0 : 1,
    });
    context.commit('setFormStep', { step: 1 });
  },

  resetForm(context: ActionContext<ImmocheckValuationState, any>) {
    context.commit('setFormData', state().data);
    context.commit('setFormStep', { step: 0 });
    context.commit('setHasUserNavigated', false);
  },

  setLead(context: ActionContext<ImmocheckValuationState, any>, payload: { id: string }): void {
    context.commit('setLead', payload as Lead);
  },

  prefetchListingDataById: async (
    context: ActionContext<ImmocheckValuationState, any>,
    {
      listingId,
      // TODO: languageCode and passThroughHeaders parameters are currently unused but may be needed for future API enhancements
      // , passThroughHeaders, languageCode
    },
  ): Promise<void> => {
    try {
      context.commit('setFormLoading', { status: true });
      context.commit('setHasError', false);
      const listingData = await prefetchListingToSftiMapping(listingId);
      let mappedListingData = null;
      if (listingData) {
        const editableProperties = Object.entries(listingData)
          .filter(([, value]) => {
            return value === null || value === undefined;
          })
          .map(([key]) => key);
        mappedListingData = {
          ...listingData,
          propertyType:
            listingData.propertyType === PropertyTypeAndUsage.SINGLE_FAMILY_HOUSE ? 1 : 0,
          propertyTypeAndUsage: listingData.propertyType,
          address: {
            lat: listingData.address.lat,
            lon: listingData.address.lon,
            countryCode: listingData.address.country,
            postalCode: listingData.address.postCode,
            city: listingData.address.townName,
            street: listingData.address.streetName,
            formattedAddress: listingData.address.formattedAddress,
          },
        };
        context.commit('setEditableProperties', editableProperties);
        context.commit('setFormData', mappedListingData);
        context.commit('setHasError', false);
      } else {
        context.commit('setHasError', true);
      }

      context.commit('setFormLoading', { status: false });
    } catch (error) {
      captureError(error);
      context.commit('setHasError', true);
      context.commit('setFormLoading', { status: false });
    }
  },

  sendImmocheckDataToUbsApi: async (
    context: ActionContext<ImmocheckValuationState, any>,
  ): Promise<void> => {
    try {
      context.commit('setSubmitInProgress', { status: true });
      const ubsPayload = mapImmocheckValuationStoreToUbsPayload(context.state.data);
      const result: { id: string } | null = await submitImmocheckFormData(ubsPayload);
      if (result && result.id) {
        context.commit('setLead', {
          id: result.id,
        });
        context.commit('setHasError', false);
        context.commit('setSubmitInProgress', { status: false });
      } else {
        throw new Error('No lead id returned from UBS Proxy API');
      }
    } catch (error) {
      captureError(error);
      context.commit('setHasError', true);
      context.commit('setSubmitInProgress', { status: false });
      context.commit('setLead', {
        id: '',
      });
    }
  },
};

function findPreviousStep(state: ImmocheckValuationState): number | null {
  const currentStep = state.step ?? NewValuationFlow.PropertyType;
  const visible = visibleSteps(state);
  const currentVisibleIndex = visible.indexOf(currentStep);

  if (currentVisibleIndex > 0) {
    return visible[currentVisibleIndex - 1];
  }

  if (
    currentStep > NewValuationFlow.UsageType &&
    currentVisibleIndex === 0 &&
    isUsageTypeFirstEditableStep(state)
  ) {
    return NewValuationFlow.UsageType;
  }

  if (currentVisibleIndex === -1) {
    return [...visible].reverse().find((candidate) => candidate < currentStep) ?? null;
  }

  return null;
}

function getLogicalFirstStep(state: Partial<ImmocheckValuationState> = {}): number {
  if (isUsageTypeFirstEditableStep(state)) {
    return NewValuationFlow.UsageType;
  }

  return visibleSteps(state)[0] ?? NewValuationFlow.PropertyType;
}

function isUsageTypeFirstEditableStep(state: Partial<ImmocheckValuationState> = {}): boolean {
  const steps = allSteps(state);
  const usageTypeIndex = steps.indexOf(NewValuationFlow.UsageType);

  if (usageTypeIndex === -1) {
    return false;
  }

  return steps.slice(0, usageTypeIndex).every((step) => isStepHidden(state, step));
}

function isPrefilledAndNonEditable(
  state: Partial<ImmocheckValuationState> = {},
  field: keyof ImmocheckValuationState['data'],
): boolean {
  const value = (state?.data as any)?.[field];
  const isPrefilled = value !== undefined && value !== null;
  const isEditable = (state?.editableProperties ?? []).includes(field as string);
  return isPrefilled && !isEditable;
}

// Simplified navigation via visible steps
function allSteps(state: Partial<ImmocheckValuationState> = {}): number[] {
  const steps = [
    NewValuationFlow.PropertyType,
    NewValuationFlow.PropertySubType,
    NewValuationFlow.UsageType,
    NewValuationFlow.YearBuild,
    NewValuationFlow.PropertyCondition,
    NewValuationFlow.ConstructionStandard,
    NewValuationFlow.Rooms,
    NewValuationFlow.LivingSpace,
  ];
  if (state?.data?.propertyType === 1) steps.push(NewValuationFlow.LotSize);
  steps.push(NewValuationFlow.Location, NewValuationFlow.SummaryPage);
  return steps;
}

function stepField(
  state: Partial<ImmocheckValuationState> = {},
  step: number,
): keyof ImmocheckValuationState['data'] | null {
  const isApt = state?.data?.propertyType === 0;
  switch (step) {
    case NewValuationFlow.PropertyType:
      return 'propertyType';
    case NewValuationFlow.PropertySubType:
      return (isApt ? 'apartmentType' : 'houseType') as any;
    case NewValuationFlow.UsageType:
      return 'classOfProperty';
    case NewValuationFlow.YearBuild:
      return 'buildYear';
    case NewValuationFlow.PropertyCondition:
      return 'stateBuildingType';
    case NewValuationFlow.ConstructionStandard:
      return 'constructionQualityType';
    case NewValuationFlow.Rooms:
      return 'rooms';
    case NewValuationFlow.LivingSpace:
      return 'livingSpace';
    case NewValuationFlow.LotSize:
      return 'landArea';
    default:
      return null; // Location, SummaryPage
  }
}

function isStepHidden(state: Partial<ImmocheckValuationState> = {}, step: number): boolean {
  if (step === NewValuationFlow.Location) return false;

  const field = stepField(state, step);
  const isStepPrefilled = field ? isPrefilledAndNonEditable(state, field) : false;

  // Don't hide the current step if user has navigated to it intentionally
  // But allow hiding it if it's pre-filled and user hasn't started navigating yet
  if (step === state.step && (!isStepPrefilled || state.hasUserNavigated)) {
    return false;
  }

  return isStepPrefilled;
}

function visibleSteps(state: Partial<ImmocheckValuationState> = {}): number[] {
  return allSteps(state).filter((s) => !isStepHidden(state, s));
}

// Remove the navigableSteps function - we'll use visibleSteps everywhere

function closestIndex(steps: number[], desired: number, dir: 'forward' | 'backward'): number {
  const i = steps.indexOf(desired);
  if (i !== -1) return i;
  if (dir === 'forward') {
    const j = steps.findIndex((s) => s > desired);
    return j === -1 ? steps.length - 1 : j;
  }
  for (let k = steps.length - 1; k >= 0; k--) if (steps[k] < desired) return k;
  return 0;
}

// previous computation is now handled directly in actions using visibleSteps

function clampStep(step: number): number {
  if (step < NewValuationFlow.PropertyType) return NewValuationFlow.PropertyType;
  if (step > NewValuationFlow.SummaryPage) return NewValuationFlow.SummaryPage;
  return step;
}

export const immocheckValuationModuleNameSpace = (action: string): string => {
  return `immocheckValuation/${action}`;
};

const immocheckValuationActions: ImmocheckValuationActionsType =
  Object.keys({
    ...actions,
    ...getters,
  }).reduce(
    (p, c) => ({
      ...p,
      [c]: immocheckValuationModuleNameSpace(c),
    }),
    {},
  ) || {};

export default {
  state,
  mutations,
  actions,
  getters,
  namespaced: true,
} as ImmocheckValuationModule;
export { immocheckValuationActions };
